import { useState, useEffect, useCallback } from "react";
import { useDataContext } from "@/context/DataContext";
import { ClinicInfo, EmergencyContact, SocialMedia, WorkingHours, SeoSettings, PixelSettings, SecuritySettings } from "@/types/cms";

export interface ClinicSettings {
  info: ClinicInfo;
  hours: WorkingHours;
  social: SocialMedia;
  emergency: EmergencyContact;
  seo: SeoSettings;
  pixels: PixelSettings;
  security: SecuritySettings;
}

// ─── Public hook (read-only, reactive) ───────────────────────────────────────
export function usePublicSettings() {
  const {
    clinicInfo: info,
    clinicHours: hours,
    clinicSocial: social,
    clinicEmergency: emergency,
    clinicSeo: seo,
    clinicPixels: pixels,
    clinicSecurity: security,
  } = useDataContext();

  return { info, hours, social, emergency, seo, pixels, security };
}

// ─── Admin CMS hook (read + write) ───────────────────────────────────────────
export function useCMSSettings() {
  const {
    clinicInfo: globalInfo,
    clinicHours: globalHours,
    clinicSocial: globalSocial,
    clinicEmergency: globalEmergency,
    clinicSeo: globalSeo,
    clinicPixels: globalPixels,
    clinicSecurity: globalSecurity,
    saveSettings,
  } = useDataContext();

  const [info, setInfo]           = useState<ClinicInfo>(globalInfo);
  const [hours, setHours]         = useState<WorkingHours>(globalHours);
  const [social, setSocial]       = useState<SocialMedia>(globalSocial);
  const [emergency, setEmergency] = useState<EmergencyContact>(globalEmergency);
  const [seo, setSeo]             = useState<SeoSettings>(globalSeo);
  const [pixels, setPixels]       = useState<PixelSettings>(globalPixels);
  const [security, setSecurity]   = useState<SecuritySettings>(globalSecurity);
  
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setInfo(globalInfo);
    setHours(globalHours);
    setSocial(globalSocial);
    setEmergency(globalEmergency);
    setSeo(globalSeo);
    setPixels(globalPixels);
    setSecurity(globalSecurity);
  }, [globalInfo, globalHours, globalSocial, globalEmergency, globalSeo, globalPixels, globalSecurity]);

  const save = useCallback(async () => {
    setSaveStatus("saving");
    try {
      await saveSettings({ info, hours, social, emergency, seo, pixels, security });
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (err) {
      setSaveStatus("error");
    }
  }, [info, hours, social, emergency, seo, pixels, security, saveSettings]);

  const updateInfo      = useCallback((d: ClinicInfo)       => { setInfo(d);      setHasChanges(true); }, []);
  const updateHours     = useCallback((d: WorkingHours)     => { setHours(d);     setHasChanges(true); }, []);
  const updateSocial    = useCallback((d: SocialMedia)      => { setSocial(d);    setHasChanges(true); }, []);
  const updateEmergency = useCallback((d: EmergencyContact) => { setEmergency(d); setHasChanges(true); }, []);
  const updateSeo       = useCallback((d: SeoSettings)      => { setSeo(d);       setHasChanges(true); }, []);
  const updatePixels    = useCallback((d: PixelSettings)    => { setPixels(d);    setHasChanges(true); }, []);
  const updateSecurity  = useCallback((d: SecuritySettings) => { setSecurity(d);  setHasChanges(true); }, []);

  const reset = useCallback(() => {
    setInfo(globalInfo);
    setHours(globalHours);
    setSocial(globalSocial);
    setEmergency(globalEmergency);
    setSeo(globalSeo);
    setPixels(globalPixels);
    setSecurity(globalSecurity);
    setHasChanges(false);
  }, [globalInfo, globalHours, globalSocial, globalEmergency, globalSeo, globalPixels, globalSecurity]);

  return {
    info, hours, social, emergency, seo, pixels, security,
    saveStatus, hasChanges,
    updateInfo, updateHours, updateSocial, updateEmergency, updateSeo, updatePixels, updateSecurity,
    save, reset,
  };
}
export type { ClinicSettings as IClinicSettings };
export type { ClinicInfo, EmergencyContact, SocialMedia, WorkingHours, SeoSettings, PixelSettings, SecuritySettings };
